import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const NoorPage: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [hoveredSkill, setHoveredSkill] = useState<string | null>(null);

  const funnyQuotes = [
    "I speak fluent JavaScript and broken English",
    "My code has more bugs than a rainforest, but it works!",
    "I debug code in my sleep... literally",
    "Coffee is my primary programming language",
    "I can fix your computer but not my own life",
    "Error 404: Social life not found"
  ];



  const achievements = [
    { title: 'Survived First Fight', description: 'Made it through our first argument without crying (much)', icon: '🥊' },
    { title: 'Master Chef', description: 'Burned water once, but Priya still loves me', icon: '👨‍🍳' },
    { title: 'Memory Champion', description: 'Remembered our anniversary (with 3 reminders)', icon: '🧠' },
    { title: 'Future Husband', description: 'Successfully convinced Priya to marry me!', icon: '💕' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-blue-400 rounded-full opacity-20"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -20, 0],
                opacity: [0.2, 0.8, 0.2],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="w-32 h-32 mx-auto mb-8 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-full flex items-center justify-center text-6xl shadow-2xl"
            >
              👨‍💻
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-5xl md:text-7xl font-bold text-white mb-4"
            >
              Hey, I'm <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Noor</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-2xl md:text-3xl text-blue-200 mb-6"
            >
              I am a Computer Nerd 🤓
            </motion.p>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="text-lg text-blue-300 max-w-3xl mx-auto"
            >
              Code by day, debug by night. I turn coffee into code and dreams into digital reality. 
              When I'm not saving the world one line of code at a time, I'm probably thinking about the next big thing in tech.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Funny Quotes Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-indigo-800 to-purple-800">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            ref={ref}
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-4xl font-bold text-center text-white mb-12"
          >
            Noor's Wisdom 💡
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {funnyQuotes.map((quote, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, rotate: Math.random() * 4 - 2 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                <p className="text-white text-lg font-medium italic">"{quote}"</p>
                <div className="flex justify-end mt-4">
                  <span className="text-blue-300 text-sm">- Noor</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Wedding Prep Skills Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-4xl font-bold text-center text-white mb-12"
          >
            My Wedding Survival Skills 💪
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { name: 'Saying "Yes Dear"', level: 95, icon: '🙋‍♂️', description: 'Mastered the art of agreement' },
              { name: 'Forgetting Everything', level: 30, icon: '🤔', description: 'Except what Priya told me 5 minutes ago' },
              { name: 'Wedding Planning', level: 20, icon: '💒', description: 'I just nod and say "That sounds perfect"' },
              { name: 'Dancing', level: 15, icon: '💃', description: 'I have two left feet but one big heart' },
              { name: 'Crying at Movies', level: 85, icon: '😭', description: 'Romantic movies hit different now' },
              { name: 'Being Grateful', level: 100, icon: '🙏', description: 'For finding my perfect match' }
            ].map((skill, index) => (
              <motion.div
                key={skill.name}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                onHoverStart={() => setHoveredSkill(skill.name)}
                onHoverEnd={() => setHoveredSkill(null)}
                className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-4xl">{skill.icon}</span>
                  <span className="text-white font-bold text-xl">{skill.level}%</span>
                </div>
                <h3 className="text-white text-xl font-bold mb-3">{skill.name}</h3>
                <div className="w-full bg-white/20 rounded-full h-3 mb-2">
                  <motion.div
                    className="bg-gradient-to-r from-cyan-400 to-blue-500 h-3 rounded-full"
                    initial={{ width: 0 }}
                    animate={inView ? { width: `${skill.level}%` } : { width: 0 }}
                    transition={{ duration: 1, delay: index * 0.1 + 0.5 }}
                  />
                </div>
                {hoveredSkill === skill.name && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-blue-200 text-sm mt-2"
                  >
                    {skill.description}
                  </motion.p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Relationship Habits Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-purple-800 to-pink-800">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-4xl font-bold text-center text-white mb-12"
          >
            My Relationship Superpowers 💕
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { name: 'Saying Sorry', icon: '🙏', description: 'Even when I\'m not sure what I did wrong' },
              { name: 'Forgetting Things', icon: '🤔', description: 'But remembering every detail about Priya' },
              { name: 'Being Wrong', icon: '😅', description: 'According to Priya, I\'m always wrong' },
              { name: 'Overthinking', icon: '🧠', description: 'Analyzing every text message for hidden meanings' },
              { name: 'Being Clumsy', icon: '🤦‍♂️', description: 'Especially when trying to be romantic' },
              { name: 'Making Her Laugh', icon: '😂', description: 'Usually by accident, but it works!' }
            ].map((habit, index) => (
              <motion.div
                key={habit.name}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, rotateY: 5 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 group"
              >
                <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  {habit.icon}
                </div>
                <h3 className="text-white text-xl font-bold mb-3">{habit.name}</h3>
                <p className="text-purple-200">{habit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-cyan-800 to-blue-900">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-4xl font-bold text-center text-white mb-12"
          >
            Relationship Victories 🏆
          </motion.h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                whileHover={{ scale: 1.02 }}
                className="bg-gradient-to-r from-cyan-600 to-blue-700 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <span className="text-4xl mr-4">{achievement.icon}</span>
                  <h3 className="text-white text-2xl font-bold">{achievement.title}</h3>
                </div>
                <p className="text-cyan-100 text-lg">{achievement.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Love Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-pink-600 to-rose-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 1 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-12 border border-white/20"
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-6xl mb-6"
            >
              💕
            </motion.div>
            <h2 className="text-4xl font-bold text-white mb-6">
              The Best Code I Ever Wrote
            </h2>
            <p className="text-xl text-pink-100 mb-8">
              Falling in love with Priya was like finding the perfect algorithm - 
              it just clicked, and now I can't imagine my life without her. 
              She's my favorite bug that I never want to fix! 🐛💕
            </p>
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-4xl"
            >
              💻 + 🩺 = 💕
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default NoorPage;
